import { defaultRates, estimateCostUsd, type Rates } from "./cost.js";

/**
 * What somebody tried before there was a project, as the admin page shows it.
 *
 * Shared with the web so the outcome recorded and the sentence the customer
 * saw cannot drift apart: "blocked" here is exactly "blocks automated readers"
 * there, and "not_found" is exactly "Check the address".
 */

/** Statuses that bot protection answers with; the site is up, it just won't talk to us. */
export const BLOCKED_STATUSES: ReadonlySet<number> = new Set([401, 403, 429, 503]);

/** The address itself is wrong, so there was nothing to read or look up. */
export const BAD_ADDRESS_CODES: ReadonlySet<string> = new Set([
  "site_unreachable",
  "bad_url",
  "bad_scheme",
  "not_public",
  "unresolvable",
]);

export type SiteReadOutcome = "read" | "no_text" | "blocked" | "error" | "timeout" | "not_found";
export type LookupOutcome = "found" | "not_found" | "failed" | "limited";

/** A read with no `code` succeeded; `foundText` then says whether it had words. */
export function siteReadOutcome(input: { code?: string; status?: number; foundText?: boolean }): SiteReadOutcome {
  const { code, status, foundText } = input;
  if (!code) return foundText === false ? "no_text" : "read";
  if (code === "site_error") return status !== undefined && BLOCKED_STATUSES.has(status) ? "blocked" : "error";
  if (code === "site_timeout") return "timeout";
  if (BAD_ADDRESS_CODES.has(code)) return "not_found";
  return "error";
}

/** Anthropic's web search: $10 per thousand searches. */
export const WEB_SEARCH_USD = 0.01;

/** Every turn of one lookup, priced: tokens at the Anthropic rates, plus each search. */
export function lookupCostUsd(
  usage: { inputTokens?: number; outputTokens?: number; searches?: number }[],
  rates: Rates = defaultRates,
): number {
  let total = 0;
  for (const turn of usage) {
    const inputTokens = turn.inputTokens ?? 0;
    const outputTokens = turn.outputTokens ?? 0;
    total += estimateCostUsd(
      { provider: "anthropic", kind: "script", unit: "tokens", quantity: inputTokens + outputTokens, inputTokens, outputTokens },
      rates,
    );
    total += (turn.searches ?? 0) * WEB_SEARCH_USD;
  }
  return Number(total.toFixed(5));
}
